import { Link, useParams } from 'react-router'
import { motion } from 'motion/react'
import { ArrowLeft } from 'lucide-react'
import { usePlayer, usePlayers } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { formatDate, initials, playerAge, plainText, POSITION_LABELS } from '@/lib/utils'
import { Container, MeshTexture, StatBlock } from '@/components/site/primitives'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingBlock } from '@/components/ui/feedback'
import { PlayerCard } from '@/components/site/cards'
import { usePageMeta } from '@/components/layout/site-layout'
import { InstagramIcon } from '@/components/site/social-icons'

export default function PlayerPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: player, isLoading, isError } = usePlayer(slug)
  const { data: squad = [] } = usePlayers()

  usePageMeta(
    player?.name,
    player
      ? plainText(player.bio, 200) || `${player.name} — ${POSITION_LABELS[player.position]}`
      : undefined
  )

  if (isLoading)
    return (
      <LoadingBlock
        className="min-h-[70vh] pt-[calc(var(--header-h)+4rem)]"
        label="Loading player"
      />
    )

  if (isError || !player) {
    return (
      <Container className="pt-[calc(var(--header-h)+5rem)] pb-24">
        <ErrorState
          title="Player not found"
          description="This player may have been removed from the squad."
          action={
            <Button asChild variant="outline">
              <Link to="/squad">Back to the squad</Link>
            </Button>
          }
        />
      </Container>
    )
  }

  const photo = fileUrl(player, player.photo, '1600x0')
  const age = playerAge(player)
  const teammates = squad.filter(
    (other) => other.id !== player.id && other.position === player.position
  )

  const stats = [
    { value: String(player.appearances || 0), label: 'Appearances' },
    player.position === 'goalkeeper'
      ? { value: String(player.clean_sheets || 0), label: 'Clean sheets' }
      : { value: String(player.goals || 0), label: 'Goals' },
    { value: String(player.assists || 0), label: 'Assists' },
  ]

  const facts = [
    { label: 'Position', value: POSITION_LABELS[player.position] },
    { label: 'Squad number', value: player.squad_number ? String(player.squad_number) : null },
    { label: 'Nationality', value: player.nationality || null },
    { label: 'Age', value: age ? `${age}` : null },
    { label: 'Height', value: player.height_cm ? `${player.height_cm} cm` : null },
    {
      label: 'Preferred foot',
      value: player.preferred_foot
        ? player.preferred_foot[0].toUpperCase() + player.preferred_foot.slice(1)
        : null,
    },
    { label: 'At the club since', value: player.joined ? formatDate(player.joined) : null },
  ].filter((fact) => fact.value)

  return (
    <>
      <section className="border-hairline bg-ink relative overflow-hidden border-b pt-[calc(var(--header-h)+2rem)] pb-0 md:pt-[calc(var(--header-h)+3rem)]">
        <MeshTexture opacity={0.05} />
        <Container className="relative">
          <Button asChild variant="ghost" size="sm" className="mb-6 -ml-3">
            <Link to="/squad">
              <ArrowLeft className="size-4" aria-hidden />
              Squad
            </Link>
          </Button>

          <div className="grid items-end gap-8 lg:grid-cols-[1fr_22rem]">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center gap-4">
                <span className="display text-blood text-[clamp(3.5rem,10vw,8rem)] leading-none">
                  {String(player.squad_number ?? 0).padStart(2, '0')}
                </span>
                <div className="pb-2">
                  <p className="eyebrow text-bone-faint">{POSITION_LABELS[player.position]}</p>
                  {player.is_captain ? (
                    <p className="condensed border-blood text-blood-bright mt-2 inline-block border px-2 py-1 text-[0.625rem] font-bold tracking-[0.16em]">
                      Captain
                    </p>
                  ) : null}
                </div>
              </div>

              <h1 className="display text-bone mt-2 text-[clamp(2.5rem,8vw,6rem)] font-bold">
                {player.name}
              </h1>

              <div className="border-hairline mt-8 flex flex-wrap gap-x-12 gap-y-6 border-t pt-8">
                {stats.map((stat) => (
                  <StatBlock key={stat.label} value={stat.value} label={stat.label} />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="bg-ink-raised relative aspect-4/5 overflow-hidden"
            >
              {photo ? (
                <img
                  src={photo}
                  alt={player.name}
                  className="size-full object-cover object-top"
                  loading="eager"
                />
              ) : (
                <div className="from-ink-raised to-ink-sunken flex size-full items-center justify-center bg-gradient-to-b">
                  <span className="display text-hairline text-[6rem]">{initials(player.name)}</span>
                </div>
              )}
              <div className="from-ink/80 pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent" />
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="border-hairline bg-ink border-b py-14 md:py-20">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_20rem]">
            <div>
              {player.bio ? (
                <div className="prose-viking" dangerouslySetInnerHTML={{ __html: player.bio }} />
              ) : (
                <p className="text-bone-faint">No profile written yet for {player.name}.</p>
              )}

              {player.instagram ? (
                <a
                  href={player.instagram}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="condensed border-hairline text-bone-dim hover:border-blood hover:text-bone mt-8 inline-flex items-center gap-2 border px-4 py-2.5 text-[0.75rem] font-semibold tracking-[0.16em] transition-colors"
                >
                  <InstagramIcon className="size-4" />
                  Follow
                </a>
              ) : null}
            </div>

            <dl className="divide-hairline border-hairline divide-y border-y">
              {facts.map((fact) => (
                <div key={fact.label} className="flex items-baseline justify-between gap-4 py-3.5">
                  <dt className="eyebrow text-bone-faint">{fact.label}</dt>
                  <dd className="condensed text-bone text-[0.9375rem] tracking-[0.04em]">
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </Container>
      </section>

      {teammates.length > 0 ? (
        <section className="bg-ink-sunken py-14 md:py-20">
          <Container>
            <h2 className="condensed text-bone mb-8 text-xl font-bold tracking-[0.1em]">
              More {POSITION_LABELS[player.position].toLowerCase()}s
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {teammates.slice(0, 4).map((other, index) => (
                <PlayerCard key={other.id} player={other} index={index} />
              ))}
            </div>
          </Container>
        </section>
      ) : null}
    </>
  )
}
